'use client';

import { useEffect, useRef } from 'react';
import { useCookieConsent } from '@/app/context/cookieConsentContext';
import { isGoogleAnalyticsReady, processGAQueue } from '@/app/utils/googleAnalytics';

export default function GoogleAnalyticsProvider() {
  const { consentGiven, isMounted } = useCookieConsent();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef(0);
  const maxAttempts = 50; // 5 seconds max (50 * 100ms)

  useEffect(() => {
    // Cleanup function to clear any existing timeout
    const cleanup = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      attemptsRef.current = 0;
    };

    if (!isMounted || typeof window === 'undefined') {
      cleanup();
      return;
    }

    if (consentGiven === 'accepted') {
      const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

      if (!measurementId) {
        // GA not configured, skip
        cleanup();
        return;
      }

      // Check if Google Analytics is ready
      if (isGoogleAnalyticsReady()) {
        // Google Analytics is ready, process any queued events
        processGAQueue();
        cleanup();
      } else {
        // Check if script already exists (server-rendered)
        const existingScript = document.querySelector(
          `script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`,
        );

        if (!existingScript) {
          // Dynamically inject GA script if not present (handles "undecided" -> "accepted" flow)
          const script = document.createElement('script');
          script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
          script.async = true;
          document.head.appendChild(script);

          // Initialize gtag
          const initScript = document.createElement('script');
          initScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${measurementId}', {
              page_path: window.location.pathname,
            });
          `;
          document.head.appendChild(initScript);
        }

        // Monitor for when GA becomes available
        const monitorGoogleAnalytics = () => {
          if (isGoogleAnalyticsReady()) {
            // Process any queued events now that GA is ready
            processGAQueue();
            cleanup();
          } else {
            attemptsRef.current++;
            if (attemptsRef.current >= maxAttempts) {
              // Timeout - GA script didn't load within expected timeframe
              cleanup();
              return;
            }
            timeoutRef.current = setTimeout(monitorGoogleAnalytics, 100);
          }
        };

        monitorGoogleAnalytics();
      }
    } else {
      // Consent not accepted, cleanup any existing timeouts
      cleanup();
    }

    // Return cleanup function for useEffect
    return cleanup;
  }, [consentGiven, isMounted]);

  // Don't render anything - this component only handles queue processing
  return null;
}
